import { useState } from "react";
import axios from "axios";
import { Scale, Send, MessageCircle } from "lucide-react";
import { BRAND } from "@/lib/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PURITIES = [
  { value: "24K", label: "24K Gold (99.9%)" },
  { value: "22K", label: "22K Gold (91.6%)" },
  { value: "18K", label: "18K Gold (75%)" },
  { value: "silver", label: "Silver" },
];

export default function RateEnquiry() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    weight: "",
    purity: "22K",
  });
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const valid = form.name.trim() && form.phone.trim() && Number(form.weight) > 0;

  const submit = (e) => {
    e.preventDefault();
    if (!valid) return;
    axios
      .post(`${API}/leads`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        weight_grams: Number(form.weight),
        purity: form.purity,
      })
      .catch(() => {});
    const purityLabel =
      PURITIES.find((p) => p.value === form.purity)?.label || form.purity;
    const msg = `Namaste! Main ${form.name.trim()} hoon (${form.phone.trim()}). Mere paas ${form.weight}g ${purityLabel} hai. Kripya aaj ka best rate bataye. (Top Rate — Sona Do Paisa Lo)`;
    window.open(
      `https://wa.me/${BRAND.whatsapp}?text=${encodeURIComponent(msg)}`,
      "_blank",
      "noopener"
    );
    setSent(true);
  };

  return (
    <section
      data-testid="rate-enquiry-section"
      className="bg-[#641C24] text-[#FFFDF8]"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
              <Scale size={13} /> Instant Rate Enquiry · तुरंत रेट पूछें
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-4 leading-[1.05]">
              Get your quote on{" "}
              <span className="italic text-[#D4AF37]">WhatsApp</span> in one
              tap.
            </h2>
            <p className="text-[#FFFDF8]/80 leading-relaxed mt-5 max-w-md">
              Tell us the weight and purity — we reply with today's best rate
              within minutes during business hours. No commitment, no visit
              needed.
            </p>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="rate-enquiry-form"
              className="bg-[#FFFDF8] text-[#2D181A] p-7 md:p-9 shadow-2xl"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#641C24]">
                    Your Name
                  </label>
                  <input
                    data-testid="rate-enquiry-name"
                    type="text"
                    value={form.name}
                    onChange={set("name")}
                    placeholder="e.g. Rajesh Sharma"
                    className="mt-2 w-full border border-[#E5DCC5] bg-white px-4 py-3 text-sm focus:outline-none focus:border-[#D4AF37]"
                    required
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#641C24]">
                    Phone Number
                  </label>
                  <input
                    data-testid="rate-enquiry-phone"
                    type="tel"
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="e.g. 98XXXXXXXX"
                    className="mt-2 w-full border border-[#E5DCC5] bg-white px-4 py-3 text-sm focus:outline-none focus:border-[#D4AF37]"
                    required
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#641C24]">
                    Approx. Weight (grams)
                  </label>
                  <input
                    data-testid="rate-enquiry-weight"
                    type="number"
                    min="0.1"
                    step="0.1"
                    value={form.weight}
                    onChange={set("weight")}
                    placeholder="e.g. 25"
                    className="mt-2 w-full border border-[#E5DCC5] bg-white px-4 py-3 text-sm focus:outline-none focus:border-[#D4AF37]"
                    required
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#641C24]">
                    Purity
                  </label>
                  <select
                    data-testid="rate-enquiry-purity"
                    value={form.purity}
                    onChange={set("purity")}
                    className="mt-2 w-full border border-[#E5DCC5] bg-white px-4 py-3 text-sm focus:outline-none focus:border-[#D4AF37]"
                  >
                    {PURITIES.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <button
                type="submit"
                data-testid="rate-enquiry-submit"
                disabled={!valid}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1FBA57] disabled:opacity-50 disabled:cursor-not-allowed text-white px-7 py-4 text-sm font-bold uppercase tracking-wider transition-colors"
              >
                <MessageCircle size={17} /> Send Enquiry on WhatsApp
              </button>

              {sent && (
                <div
                  data-testid="rate-enquiry-success"
                  className="mt-4 flex items-center gap-2 text-sm text-[#0F7A3B] font-semibold"
                >
                  <Send size={14} /> Enquiry sent! WhatsApp opened — we'll
                  reply with today's best rate shortly.
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
